import { inject, Injectable, InjectionToken } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { combineLatest, map, Observable, of, shareReplay, switchMap } from 'rxjs'
import { BROWSER_STORAGE, CachedReferenceService, SearchFn } from 'src/app/public/reference-data/base-reference.service'
import { ReferenceDataService } from 'src/app/public/reference-data/reference-data.service'
import { ServicePointGroup, ServicePointGroupUpsertRequest, ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ServicePointIdReference } from '../../shared/models/referenceData'

const arrayToMapRx = <T>(key: (i: T) => string) => map<T[], Record<string, T>>((arr) => arr.reduce((acc, next) => ({ ...acc, [key(next)]: next }), {})) 

function searchServicePointGroups(item: ServicePointGroup, term?: string | undefined): boolean {
  if(term) {
    const terms = term.split(',')
    terms.some(term => item.service_point_group_name.toLowerCase().startsWith(term))
  }
  return true
}

export const GROUP_SEARCH = new InjectionToken<SearchFn<ServicePointGroup>>(
  'Group Search',
  {
    providedIn: 'root',
    factory: () => searchServicePointGroups,
  },
)

const CACHED_SERVICE_POINT_GROUP_DATA = new InjectionToken<CachedReferenceService<ServicePointGroup>>(
  'dashboards',
  {
    providedIn: 'root',
    factory: () => new CachedReferenceService<ServicePointGroup>(
      inject(HttpClient), 
      inject(BROWSER_STORAGE),
      'service_point_group/',
      inject(GROUP_SEARCH),
      undefined,
      [{ name: 'service_point_group_name' }],
    ),
  },
)

@Injectable({ providedIn: 'root' })
export class ServicePointGroupService {
  private readonly httpClient = inject(HttpClient)
  private readonly servicePointGroups = inject(CACHED_SERVICE_POINT_GROUP_DATA)
  private readonly referenceDataService = inject(ReferenceDataService)

  data$ = this.servicePointGroups
    .get()
    .pipe(
      switchMap(groups =>
        combineLatest([
          of(groups),
          this.getServicePoints(Array.from(new Set(groups.flatMap(g => g.service_point_uid ?? [])))),
          this.referenceDataService.data('companies').pipe(arrayToMapRx(e => e.entity_code)),
          this.referenceDataService.data('controlAreas').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('cpNodes').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('deliveryClasses').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('localDistributionCompanies').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('locations').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('lossClasses').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('meterTypes').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('profileClasses').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('retailers').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('servicePointSources').pipe(arrayToMapRx(e => e.entity_uid)),
          this.referenceDataService.data('supplyClasses').pipe(arrayToMapRx(e => e.entity_uid)),
        ]),
      ),
      map(([
        groups,
        servicePoints,
        companies,
        controlAreas,
        cpNodes,
        deliveryClasses,
        localDistributionCompanies,
        locations,
        lossClasses,
        meterTypes,
        profileClasses,
        retailers,
        servicePointSources,
        supplyClasses,
      ]): ServicePointGroupWithRefs[] => {
        return groups.map(g => ({
          service_point_group_uid: g.service_point_group_uid,
          service_point_group_name: g.service_point_group_name,
          service_point_uid: g.service_point_uid?.map(s => servicePoints[s]).filter(Boolean),
          service_point_id_search: g.service_point_id_search,
          company_code: g.company_code?.map(e => companies[e]).filter(Boolean),
          control_area_uid: g.control_area_uid?.map(e => controlAreas[e]).filter(Boolean),
          cpnode_uid: g.cpnode_uid?.map(e => cpNodes[e]).filter(Boolean),
          delivery_class_uid: g.delivery_class_uid?.map(e => deliveryClasses[e]).filter(Boolean),
          eldc_uid: g.eldc_uid?.map(e => localDistributionCompanies[e]).filter(Boolean),
          ldc_uid: g.ldc_uid?.map(e => localDistributionCompanies[e]).filter(Boolean),
          location_uid: g.location_uid?.map(e => locations[e]).filter(Boolean),
          loss_class_uid: g.loss_class_uid?.map(e => lossClasses[e]).filter(Boolean),
          meter_type_uid: g.meter_type_uid?.map(e => meterTypes[e]).filter(Boolean),
          profile_class_uid: g.profile_class_uid?.map(e => profileClasses[e]).filter(Boolean),
          retailer_uid: g.retailer_uid?.map(e => retailers[e]).filter(Boolean),
          service_point_source_uid: g.service_point_source_uid?.map(e => servicePointSources[e]).filter(Boolean),
          supply_class_uid: g.supply_class_uid?.map(e => supplyClasses[e]).filter(Boolean),
        }))
      }),
      shareReplay(1),
    )

  constructor() {
    this.servicePointGroups.init()
    this.data$.pipe(takeUntilDestroyed()).subscribe()
  }

  private getServicePoints(servicePointUids: string[]): Observable<Record<string, ServicePointIdReference>> {
    if (servicePointUids.length === 0) {
      return of({})
    } else {
      return this.referenceDataService.servicePoints(
        { pageSize: servicePointUids.length, pageNumber: 1 },
        [{ name: 'service_point_uid', values: [{ operation: 'in=', value: servicePointUids.join(',') }] }],
      ).pipe(arrayToMapRx(s => s.service_point_uid))
    }
  }

  get() {
    return this.data$
  }

  delete(service_point_group_uid: string): Observable<unknown> {
    return this.httpClient.delete(`service_point_group/${service_point_group_uid}`)
  }

  update(
    service_point_group_uid: string,
    req: ServicePointGroupUpsertRequest,
  ): Observable<unknown> {
    return this.httpClient.put(`service_point_group/${service_point_group_uid}`, req)
  }

  create(req: ServicePointGroupUpsertRequest): Observable<unknown> {
    return this.httpClient.post('service_point_group/', req)
  }

  refresh() {
    this.servicePointGroups.refresh()
  }
}


